import { useState } from "react";
import type { BuyerPrequalificationAttemptDto } from "@/api/buyers";
import {
  CategoryBadge,
  ExceptionBadge,
  Skeleton,
  StateMark,
  StatusBadge,
  SubTable,
  Surface,
  TableValue,
  Timestamp,
  type SubTableColumn,
} from "@/design-system/components/ui";
import { qualifiedAmountLabel } from "./qualifiedAmount";
import { resolvedShortListingName } from "@/lib/listingDisplay";
import { useBuyerPrequalificationAttempts } from "@/hooks/buyers/usePrequalificationInvites";
import { DetailSection } from "../detailBits";
import { InlineQueryError } from "../queryStates";
import { PrequalificationAnswersDrawer } from "./PrequalificationAnswersDrawer";

/**
 * Every prequalification this buyer completed.
 *
 * The collection alongside the single primary pointer the Overview summary
 * shows. Each row carries the LISTING it was sized against, which is the whole
 * reason the attempts collection is a Buyer-module table rather than a column on
 * the prequalification submission — a letter sized for a $2M business attached
 * to a $600K submission is the failure the override exists to prevent, and the
 * picker cannot warn about what it cannot see.
 *
 * An attempt whose submission has been purged renders as `No longer available`
 * rather than being dropped: the buyer did complete it, and a silently shorter
 * list is a quieter lie than an honest gap.
 */
/** How this attempt reached the buyer. `StaffSent` is a staff member sending
 *  the completion to the assignment queue from the prequalification page —
 *  the send that CREATED this buyer — and must not read as "linked", which is
 *  attaching a completion to a buyer that already existed. Exported for its
 *  test. */
export function attemptSourceLabel(source: string): string {
  switch (source) {
    case "FunnelCompletion":
      return "From the funnel";
    case "StaffSent":
      return "Sent by staff";
    // A Buyer Consultant ran the prequalification on a telephone call from the
    // buyer record (the one-call brief).
    case "StaffCall":
      return "Prequalified on a call";
    default:
      return "Linked by staff";
  }
}

export function AttemptsSection({ buyerLeadId }: { buyerLeadId: string }) {
  const attempts = useBuyerPrequalificationAttempts(buyerLeadId);
  const rows = attempts.data ?? [];

  /** The attempt whose answers are open, or null. Local state, not a search
   *  param: this list already renders inside the buyer drawer on some surfaces,
   *  and the page underneath owns the URL's one record key. */
  const [openAttempt, setOpenAttempt] = useState<BuyerPrequalificationAttemptDto | null>(
    null,
  );

  return (
    <DetailSection title="Prequalifications completed">
      <div data-testid="prequal-attempts" className="grid gap-space-3">
        <p className="m-0 text-caption text-gray-mid">
          Every attempt on file. The one marked primary is the letter a package
          uses unless a submission overrides it.
        </p>

        {attempts.isLoading ? (
          <Skeleton className="h-24 w-full" />
        ) : attempts.isError ? (
          /* "No prequalifications yet" is a claim about the buyer's history — and
             the wrong one to make on a failed read, because it invites a
             specialist to send an invitation the buyer may already have used. */
          <InlineQueryError
            what="the completed prequalifications"
            onRetry={() => void attempts.refetch()}
            retrying={attempts.isFetching}
          />
        ) : (
          // `DetailSection` publishes no surface of its own, so without this the
          // table would resolve to "page" and paint the screen's own full-onyx
          // header inside a card. Declared here rather than pinned with the
          // table's `surface` escape hatch, which would survive the section
          // being moved into a drawer and be wrong there.
          <Surface kind="card">
            <SubTable
              caption="Prequalifications completed"
              data-testid="prequal-attempts-table"
              rows={rows}
              rowKey={(a) => a.id}
              rowTestId={() => "prequal-attempt-row"}
              columns={ATTEMPT_COLUMNS}
              /* The whole row is the door. An attempt whose submission has been
                 purged has nothing behind it, so it stays inert rather than
                 opening a drawer that could only say so. `SubTable` supplies the
                 keyboard half — the row becomes a tab stop and takes
                 Enter/Space. */
              onRowClick={(a) => {
                if (!a.unavailable) setOpenAttempt(a);
              }}
              empty={{
                message: "No prequalifications yet",
                // 🔴 Prose, never a control. Nothing on this list can create an
                // attempt — a buyer completing an invitation is what fills it —
                // so the "derived" door with no `onClick` is the right shape:
                // name what will fill it, offer nothing that cannot.
                door: {
                  kind: "derived",
                  explain:
                    "Send an invitation, or link one this buyer completed before Phoenix knew about them.",
                },
              }}
            />
          </Surface>
        )}
      </div>

      <PrequalificationAnswersDrawer
        submissionId={openAttempt?.submissionId ?? null}
        title={openAttempt ? attemptLabel(openAttempt) : ""}
        subtitle={
          openAttempt?.outcome ? `Outcome: ${openAttempt.outcome}` : undefined
        }
        onClose={() => setOpenAttempt(null)}
      />
    </DetailSection>
  );
}

/**
 * An attempt with no listing behind it, in the ONE phrasing.
 *
 * <p>🔴 <b>Table and Tile Standard v1.0 §6 "state absent" — muted grey prose,
 * never a pill, and no door.</b> Not §7's plain-missing-value em dash: §7.1's
 * test is whether a person could do something about it, and this is not an
 * uncollected field but a normal, expected condition of the buyer's journey —
 * prequalifying before finding a business is the usual first step. There is no
 * "tie a listing to this attempt" action to offer anywhere (the link/unlink
 * hooks tie a prequalification to a BUYER), so the cell states the fact and
 * stops.</p>
 *
 * <p>It is a constant because the same null was being written two ways —
 * "Not tied to a listing" here and "no listing" in the override picker — so one
 * attempt read as two different facts depending on which surface you met it on.
 * The picker's label is an <c>&lt;option&gt;</c>, which can carry text and
 * nothing else, so prose is also the only treatment available to both.</p>
 */
export const NOT_TIED_TO_A_LISTING = "Not tied to a listing";

/**
 * One attempt as a single line: reference, then the listing it was sized
 * against.
 *
 * <p>⚠️ The comment here used to claim this was "shared with the override
 * picker so the two can never describe the same attempt differently." It never
 * was — this function has no callers at all, and the picker builds its own
 * `describe`, which it needs because an `<option>` must also carry the
 * qualified amount. What the two DO now share is
 * {@link NOT_TIED_TO_A_LISTING}, which is the part they were actually
 * disagreeing about.</p>
 *
 * <p>🔴 <b>The SHORT variant, and the em dash is the reason it has to be cut
 * inside the interpolation rather than around it.</b> This label already joins
 * with an em dash, and the em dash is exactly what a marketplace title uses to
 * staple its pitch on — "…ATM Portfolio — $820K Annual Cash Flow". Shortening
 * the composed string would cut at the FIRST em dash, which is this function's
 * own separator, and collapse the whole label to the reference number alone.
 * So the name is shortened first and joined second.</p>
 */
export function attemptLabel(a: BuyerPrequalificationAttemptDto): string {
  if (a.unavailable) return "No longer available";
  const ref = a.referenceNumber ?? "Prequalification";
  const name = resolvedShortListingName(a.listingDisplayName, a.listingBusinessName);
  return name ? `${ref} — ${name}` : ref;
}

/**
 * The columns, and the ONE badge vocabulary.
 *
 * <p>🔴 <b>The row used to carry two colliding badge systems.</b> `Primary` was
 * the staff `Badge` — 10px, uppercase, square-cornered, solid gold — sitting
 * immediately beside a `StatusBadge` at 13px in a bordered pill, so two marks on
 * one line announced themselves as two different KINDS of thing when the only
 * real difference was which component the author reached for. Table and Tile
 * Standard v1.0 §6 gives four marks and four meanings, and every mark here is
 * now one of them:</p>
 *
 * <ul>
 *   <li><b>Primary</b> — CATEGORY. Which of the buyer's attempts this one is;
 *       it does not change on its own. Gold-on-black pill.</li>
 *   <li><b>Outcome</b> — STATE. The engine's verdict on the submission.</li>
 *   <li><b>No longer available</b> — EXCEPTION. Something is wrong with this
 *       row: its submission has been purged. Outline pill, inline after the row's
 *       own name, which is where the eye already is when scanning for which row
 *       to work. It was a muted `StatusBadge` standing IN PLACE OF the outcome,
 *       which read as a verdict rather than as a fault.</li>
 *   <li><b>Letter</b> — STATE present / absent. Absent is muted prose, never a
 *       grey pill: a pill is a claim that a fact was recorded, and "no letter"
 *       is the absence of one. It used to render nothing at all when absent, so
 *       an attempt with no letter and an attempt whose letter column had not
 *       loaded looked identical.</li>
 * </ul>
 */
const ATTEMPT_COLUMNS: SubTableColumn<BuyerPrequalificationAttemptDto>[] = [
  {
    key: "reference",
    header: "Prequalification",
    className: "align-top",
    sortAccessor: (a) => a.referenceNumber ?? "",
    searchText: (a) => a.referenceNumber ?? "Prequalification",
    render: (a) => (
      <span className="flex flex-wrap items-center gap-space-2">
        <span className="text-small text-onyx">
          {a.referenceNumber ?? "Prequalification"}
        </span>
        {a.isPrimary && <CategoryBadge>Primary</CategoryBadge>}
        {a.unavailable && <ExceptionBadge>No longer available</ExceptionBadge>}
      </span>
    ),
  },
  {
    key: "outcome",
    header: "Outcome",
    className: "align-top",
    sortAccessor: (a) => a.outcome ?? "",
    searchText: (a) => a.outcome ?? "",
    // An unavailable attempt reports no outcome: the submission it belonged to
    // is gone, so the verdict is not ours to state any more. A plain missing
    // value, because there is nothing anyone could do to recover it.
    render: (a) => (
      <TableValue
        value={
          !a.unavailable && a.outcome ? (
            <StatusBadge tone={a.outcome === "Pass" ? "ok" : "warn"}>{a.outcome}</StatusBadge>
          ) : null
        }
      />
    ),
  },
  {
    key: "listing",
    header: "Listing",
    className: "align-top",
    searchText: (a) =>
      resolvedShortListingName(a.listingDisplayName, a.listingBusinessName) ?? "",
    /*
      🔴 What this letter was sized against, through the ONE ladder — business
      name → headline → the listing URL's host. Printing the raw business-name
      column dropped a listing whose seller has not named the business out of the
      ladder entirely, so it read as "not tied to a listing" when it was tied to
      one with a headline.

      The clause is kept whole — "Sized against X" rather than a bare name — so
      it stays a matched pair with the absent case. "Valley HVAC" beside "Not
      tied to a listing" is two different kinds of sentence; both surfaces that
      state this fact say it as a relationship, and the header names the column
      rather than replacing the phrasing.
    */
    render: (a) => {
      const listingName = resolvedShortListingName(
        a.listingDisplayName,
        a.listingBusinessName,
      );
      return listingName ? (
        <span className="text-caption text-gray-mid">{`Sized against ${listingName}`}</span>
      ) : (
        <span className="text-caption text-gray-mid">{NOT_TIED_TO_A_LISTING}</span>
      );
    },
  },
  {
    key: "qualified",
    header: "Qualified",
    align: "right",
    className: "align-top",
    sortAccessor: (a) => a.qualifiedAmount ?? null,
    searchText: (a) => qualifiedAmountLabel(a) ?? "",
    // `qualifiedAmountLabel` is the redaction-aware read: a caller without
    // `Prequalification.Read` gets "Restricted" rather than a blank, because a
    // blank is indistinguishable from an unsized prequalification and sends the
    // reader hunting for one that is not missing.
    render: (a) => <TableValue value={qualifiedAmountLabel(a)} />,
  },
  {
    key: "recorded",
    header: "Recorded",
    className: "align-top",
    sortAccessor: (a) => (a.submittedAtUtc ? new Date(a.submittedAtUtc) : null),
    searchText: (a) => attemptSourceLabel(a.source),
    render: (a) => (
      <span className="grid gap-space-1 text-caption text-gray-mid">
        {a.submittedAtUtc && (
          <span>
            Completed <Timestamp value={a.submittedAtUtc} />
          </span>
        )}
        <span>{attemptSourceLabel(a.source)}</span>
      </span>
    ),
  },
  {
    key: "letter",
    header: "Letter",
    className: "align-top",
    sortAccessor: (a) => (a.hasLetter ? 1 : 0),
    searchText: (a) => (a.hasLetter ? "Letter on file" : "No letter"),
    render: (a) => (
      <StateMark present={a.hasLetter} presentLabel="Letter on file" absentLabel="No letter" />
    ),
  },
];
